/*
  Offertemand (quote basket). Items are stored in localStorage so they survive page loads.
  - [data-sv-quote-add] + data-sv-quote-item='{json}'   adds one item (quantity 1)
      optional data-sv-quote-variants='[...]'            read the variant/quantity chosen in the product form
  - [data-sv-quote-open]                                opens the drawer
  - [data-sv-quote-count]                               shows the number of lines
  - <sv-quote-list-view>                                editable list on the quote page (fills a hidden form field)
  - window.SvQuote.add(items, { open })                 used by the bundle builder
*/
(() => {
  if (window.SvQuote) return;

  const KEY = 'sv-quote-list-v1';
  const EVENT = 'sv-quote:change';

  const read = () => {
    try {
      const list = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  };

  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      /* storage full or blocked: keep working in memory for this page */
    }
    memory = list;
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { items: list } }));
  };

  let memory = read();
  const items = () => memory;

  const add = (newItems, options = {}) => {
    const list = items().slice();
    newItems.forEach((item) => {
      if (!item || !item.id) return;
      const qty = Math.max(1, parseInt(item.qty, 10) || 1);
      const existing = list.find((i) => i.id === item.id);
      if (existing) existing.qty = Math.min(999, existing.qty + qty);
      else list.push({ ...item, qty });
    });
    write(list);
    if (options.open) drawer()?.open();
    else drawer()?.toast(newItems.length);
  };

  const setQty = (id, qty) => {
    const list = items()
      .map((i) => (i.id === id ? { ...i, qty: Math.max(0, Math.min(999, qty)) } : i))
      .filter((i) => i.qty > 0);
    write(list);
  };

  const remove = (id) => write(items().filter((i) => i.id !== id));
  const clear = () => write([]);

  const lineText = (i) => {
    let text = `${i.qty}x ${i.product}`;
    if (i.variant) text += ` (${i.variant})`;
    if (i.sku) text += `, art.nr. ${i.sku}`;
    return text;
  };

  const asText = () => items().map(lineText).join('\n');

  const fmt = (cents) => {
    const el = document.querySelector('sv-quote-drawer');
    const currency = el?.dataset.currency || 'EUR';
    const locale = el?.dataset.locale || 'nl';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Math.round(cents) / 100);
  };

  const exclVat = (cents) => (cents * 100) / 121;

  const escapeHtml = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const lineHtml = (i) => `
    <li class="sv-qline" data-id="${i.id}">
      <a class="sv-qline__img" href="${escapeHtml(i.url)}" tabindex="-1" aria-hidden="true">
        ${i.image ? `<img src="${escapeHtml(i.image)}" alt="" width="64" height="64" loading="lazy">` : ''}
      </a>
      <div class="sv-qline__info">
        <a class="sv-qline__title" href="${escapeHtml(i.url)}">${escapeHtml(i.product)}</a>
        ${i.variant ? `<span class="sv-qline__meta">${escapeHtml(i.variant)}</span>` : ''}
        ${i.sku ? `<span class="sv-qline__meta">Art.nr. ${escapeHtml(i.sku)}</span>` : ''}
        ${i.price ? `<span class="sv-qline__price">${fmt(exclVat(i.price * i.qty))} <small>excl. BTW</small></span>` : ''}
      </div>
      <div class="sv-qline__actions">
        <div class="sv-qline__qty" role="group" aria-label="Aantal ${escapeHtml(i.product)}">
          <button type="button" data-q-step="-1" aria-label="Minder">−</button>
          <input type="number" min="1" max="999" value="${i.qty}" inputmode="numeric" aria-label="Aantal" data-q-input>
          <button type="button" data-q-step="1" aria-label="Meer">+</button>
        </div>
        <button type="button" class="sv-qline__remove" data-q-remove>Verwijder</button>
      </div>
    </li>`;

  /* Shared handlers for the editable lists (drawer + quote page) */
  const bindList = (root) => {
    root.addEventListener('click', (e) => {
      const line = e.target.closest('[data-id]');
      if (!line) return;
      const id = parseInt(line.dataset.id, 10);
      const current = items().find((i) => i.id === id);
      if (!current) return;
      if (e.target.closest('[data-q-remove]')) remove(id);
      const step = e.target.closest('[data-q-step]');
      if (step) setQty(id, Math.max(1, current.qty + parseInt(step.dataset.qStep, 10)));
    });
    root.addEventListener('change', (e) => {
      if (!e.target.matches('[data-q-input]')) return;
      const id = parseInt(e.target.closest('[data-id]').dataset.id, 10);
      setQty(id, Math.max(1, parseInt(e.target.value, 10) || 1));
    });
  };

  const updateCounts = () => {
    const count = items().length;
    document.querySelectorAll('[data-sv-quote-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  };

  /* Drawer */
  class SvQuoteDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.list = this.querySelector('[data-sv-quote-items]');
      this.toastEl = this.querySelector('[data-sv-quote-toast]');
      this.querySelector('[data-sv-quote-toast] [data-sv-quote-open]').textContent = this.dataset.view;
      bindList(this.list);
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-sv-quote-close]') || e.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('close', () => document.documentElement.classList.remove('sv-qd-open'));
      document.addEventListener(EVENT, () => this.render());
      this.render();
    }

    render() {
      const list = items();
      this.list.innerHTML = list.map(lineHtml).join('');
      this.querySelector('[data-sv-quote-empty]').hidden = list.length > 0;
      this.querySelector('[data-sv-quote-foot]').hidden = list.length === 0;
      const total = list.reduce((sum, i) => sum + (i.price || 0) * i.qty, 0);
      this.querySelector('[data-sv-quote-total]').textContent = fmt(exclVat(total));
    }

    open() {
      this.hideToast();
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('sv-qd-open');
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    toast(n) {
      this.querySelector('[data-sv-quote-toast-text]').textContent =
        n > 1 ? `${n} artikelen ${this.dataset.added.charAt(0).toLowerCase()}${this.dataset.added.slice(1)}` : this.dataset.added;
      this.toastEl.hidden = false;
      requestAnimationFrame(() => this.toastEl.classList.add('is-visible'));
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.hideToast(), 4500);
    }

    hideToast() {
      this.toastEl.classList.remove('is-visible');
      clearTimeout(this.timer);
      this.timer = setTimeout(() => (this.toastEl.hidden = true), 250);
    }
  }

  /* Editable list on the quote page */
  class SvQuoteListView extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-sv-quote-items]');
      this.field = this.querySelector('[data-sv-quote-field]');
      bindList(this.list);
      this.querySelector('[data-sv-quote-clear]')?.addEventListener('click', () => clear());
      if (this.hasAttribute('data-clear')) clear();
      document.addEventListener(EVENT, () => this.render());
      this.render();
    }

    render() {
      const list = items();
      this.hidden = list.length === 0;
      this.list.innerHTML = list.map(lineHtml).join('');
      if (this.field) this.field.value = asText();
    }
  }

  const drawer = () => document.querySelector('sv-quote-drawer');

  /* Add buttons */
  const itemFromButton = (btn) => {
    let item;
    try {
      item = JSON.parse(btn.dataset.svQuoteItem);
    } catch (e) {
      return null;
    }
    if (btn.dataset.svQuoteVariants) {
      try {
        const variants = JSON.parse(btn.dataset.svQuoteVariants);
        const scope = btn.closest('.shopify-section, product-info, main') || document;
        const idInput = scope.querySelector('form[action*="/cart/add"] [name="id"]');
        const qtyInput = scope.querySelector('form[action*="/cart/add"] [name="quantity"], quantity-selector-component input');
        const variant = idInput && variants.find((v) => String(v.id) === String(idInput.value));
        if (variant) Object.assign(item, { id: variant.id, variant: variant.title, sku: variant.sku, price: variant.price });
        if (qtyInput) item.qty = parseInt(qtyInput.value, 10) || 1;
      } catch (e) {
        /* fall back to the default variant */
      }
    }
    return item;
  };

  document.addEventListener('click', (e) => {
    const addBtn = e.target.closest('[data-sv-quote-add]');
    if (addBtn) {
      e.preventDefault();
      const item = itemFromButton(addBtn);
      if (item) {
        add([item]);
        addBtn.classList.add('is-added');
        setTimeout(() => addBtn.classList.remove('is-added'), 1600);
      }
      return;
    }
    const openBtn = e.target.closest('[data-sv-quote-open]');
    if (openBtn && drawer()) {
      e.preventDefault();
      drawer().open();
    }
  });

  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return;
    memory = read();
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { items: memory } }));
  });

  document.addEventListener(EVENT, updateCounts);

  window.SvQuote = { add, items, remove, clear, setQty, asText };

  customElements.define('sv-quote-drawer', SvQuoteDrawer);
  customElements.define('sv-quote-list-view', SvQuoteListView);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', updateCounts);
  else updateCounts();
})();
